import {
  CalendarOutlined,
  EnvironmentOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Post } from '@frontend/data-access';
import {
  formatDateWithTimezone,
  getLinkLabel,
  timezoneStringToGmtOffsetString,
} from '@frontend/shared/util';
import { Space, Steps, Typography } from 'antd';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { RRule, RRuleSet, rrulestr } from 'rrule';

const { Text } = Typography;

function EventInfo({
  title,
  data,
  type,
}: {
  title: string;
  data: Post;
  type: 'ADMIN' | 'USER';
}) {
  const { t: tcommon } = useTranslation('common');
  const [rule, setRule] = useState<RRule | RRuleSet>();

  useEffect(() => {
    if (data.recurrenceConfig) {
      const rruleLine = data.recurrenceConfig.match(/RRULE:.+/)?.[0] as string;
      setRule(
        rrulestr(rruleLine, {
          dtstart: dayjs(data.eventStartDate).toDate(),
        })
      );
    }
  }, [data.eventStartDate, data.recurrenceConfig]);

  const TimeLine = () => {
    if (data.recurrenceConfig) {
      const occurrences = rule?.all() ?? [];
      const now = new Date();
      const current = occurrences.findIndex((occ) => occ > now);
      const currentStep = current === -1 ? occurrences.length - 1 : current;

      const stepItems = occurrences.map((item, idx) => ({
        title: (
          <div key={idx} className="flex items-center">
            <Space size={4} split={<Text type="secondary">–</Text>}>
              <Text>
                {formatDateWithTimezone(
                  item.toUTCString(),
                  data.eventTimezone,
                  data.allDay ? 'D MMM YY' : 'ddd, DD MMM YY HH:mm'
                )}
              </Text>

              {!!data.eventEndDate && (
                <Text>
                  {formatDateWithTimezone(
                    data.eventEndDate,
                    data.eventTimezone,
                    data.allDay ? 'D MMM YY' : ' HH:mm'
                  )}
                </Text>
              )}
            </Space>

            <Text type="secondary" className="text-xs font-normal ml-2">
              ({timezoneStringToGmtOffsetString(data.eventTimezone)})
            </Text>
          </div>
        ),
      }));

      return (
        <div>
          <div className="flex items-center">
            <CalendarOutlined className="mr-4" />
            <Space size={4} split={<Text type="secondary">–</Text>}>
              <Text strong>
                {rule?.toText() && getLinkLabel(rule?.toText())}
              </Text>
            </Space>
          </div>
          <div className="px-1">
            <Steps
              progressDot
              current={currentStep}
              direction="vertical"
              items={stepItems}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center">
          <CalendarOutlined className="mr-4" />
          <Space size={4} split={<Text type="secondary">–</Text>}>
            <Text>
              {formatDateWithTimezone(
                data.eventStartDate,
                data.eventTimezone,
                data.allDay ? 'D MMM YY' : 'D MMM YY HH:mm'
              )}
            </Text>

            {!!data.eventEndDate && (
              <Text>
                {formatDateWithTimezone(
                  data.eventEndDate,
                  data.eventTimezone,
                  data.allDay ? 'D MMM YY' : 'D MMM YY HH:mm'
                )}
              </Text>
            )}
          </Space>

          <Text type="secondary" className="text-xs font-normal ml-2">
            ({timezoneStringToGmtOffsetString(data.eventTimezone)})
          </Text>
        </div>
      );
    }
  };

  return (
    <div className="space-y-2">
      <h4 className="text-base font-semibold">{title}</h4>
      <div className="flex items-center font-semibold">
        <EnvironmentOutlined className="mr-4" />
        <div>{data.eventLocation}</div>
      </div>
      <TimeLine />
      {type === 'ADMIN' && (
        <div className="flex items-center font-semibold">
          <UserOutlined className="mr-4" />
          <div>
            {tcommon('event_members_joined', {
              count: data?.rsvpList ? data?.rsvpList.length : 0,
            })}
          </div>
        </div>
      )}
      <div className="text-gray-700 font-normal">
        {tcommon('event_register_by') + ' '}
        {formatDateWithTimezone(
          data.eventRegistrationDate,
          data.eventTimezone,
          'D MMM YY, HH:mm'
        )}{' '}
        {timezoneStringToGmtOffsetString(data?.eventTimezone)}
      </div>
    </div>
  );
}

export default EventInfo;
